"use client";

import { motion, useScroll, useTransform } from "framer-motion";
import { useState, useEffect, useMemo } from "react";
import Image from "next/image";
import { fadeInUp, staggerContainer } from "@/lib/animations";
import {
  MapPin,
  Sparkles,
  Calendar,
  Menu,
  Search,
  X,
  Loader2,
  ShoppingCart,
  Check,
  Plus,
  Minus,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTheme } from "@/components/ThemeProvider";
import { useCartStore } from "@/app/store/cartStore";
import menuService, {
  MenuCategory,
  MenuItem,
} from "@/app/services/menuService";
import { useAPI } from "@/app/hooks";
import CartButton from "@/components/ui/CartButton";

export default function Hero() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  const [menuOpen, setMenuOpen] = useState(false);
  const [eventsOpen, setEventsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [addedItems, setAddedItems] = useState<Set<string>>(new Set());
  const [itemQuantities, setItemQuantities] = useState<Record<string, number>>(
    {},
  );
  const [cartOpen, setCartOpen] = useState(false);

  const { theme } = useTheme();
  const [resolvedTheme, setResolvedTheme] = useState<"light" | "dark">("light");

  // Cart functionality
  const addToCart = useCartStore((state) => state.addItem);
  const getItemCount = useCartStore((state) => state.getItemCount);

  // Get quantity for an item (from local state or default 1)
  const getQuantity = (itemId: string) => itemQuantities[itemId] || 1;

  // Update quantity in local state
  const updateLocalQuantity = (itemId: string, quantity: number) => {
    if (quantity < 1) quantity = 1;
    if (quantity > 99) quantity = 99;
    setItemQuantities((prev) => ({ ...prev, [itemId]: quantity }));
  };

  // Scroll to events
  const scrollToEvents = () => {
    const eventsSection = document.getElementById("events");
    if (eventsSection) {
      const offset = 80;
      const elementPosition = eventsSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  // Handler for adding items to cart
  const handleAddToCart = (item: MenuItem) => {
    const quantity = getQuantity(item.id);

    // Add to cart multiple times based on quantity
    for (let i = 0; i < quantity; i++) {
      addToCart({
        item_id: item.id,
        name: item.name,
        name_ar: item.name_ar,
        price: item.price,
        image: item.image,
        category: item.category?.name,
      });
    }

    // Reset quantity to 1 after adding
    setItemQuantities((prev) => ({ ...prev, [item.id]: 1 }));

    // Show visual feedback
    setAddedItems((prev) => new Set(prev).add(item.id));
    setTimeout(() => {
      setAddedItems((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }, 2000);
  };

  // Fetch menu items
  const {
    data: menuResponse,
    loading: menuLoading,
    error: menuError,
    refetch: refetchMenu,
  } = useAPI(
    () =>
      menuService.getMenuItems({
        pageNumber: 1,
        limit: 100,
      }),
    [],
  );

  // Fetch categories
  const { data: categoriesResponse, loading: categoriesLoading } = useAPI(
    () =>
      menuService.getCategories({
        pageNumber: 1,
        limit: 100,
      }),
    [],
  );

  const menuItems: MenuItem[] = menuResponse?.rows || [];
  const categories: MenuCategory[] = categoriesResponse?.rows || [];

  useEffect(() => {
    if (theme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? "dark"
        : "light";
      setResolvedTheme(systemTheme);

      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handler = (e: MediaQueryListEvent) => {
        setResolvedTheme(e.matches ? "dark" : "light");
      };
      mediaQuery.addEventListener("change", handler);
      return () => mediaQuery.removeEventListener("change", handler);
    } else {
      setResolvedTheme(theme as "light" | "dark");
    }
  }, [theme]);

  const logoSrc =
    resolvedTheme === "dark"
      ? "/images/IDWorksLogo.svg"
      : "/images/idworks-logo.png";

  const filteredItems = useMemo(() => {
    return menuItems.filter((item) => {
      const matchesCategory =
        selectedCategory === "All" || item.category_id === selectedCategory;

      const matchesSearch =
        !searchQuery ||
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.name_ar?.includes(searchQuery) ||
        false;

      return matchesCategory && matchesSearch;
    });
  }, [menuItems, selectedCategory, searchQuery]);

  const itemsByCategory = useMemo(() => {
    const grouped: Record<string, MenuItem[]> = {};

    filteredItems.forEach((item) => {
      const categoryId = item.category_id;
      if (!grouped[categoryId]) {
        grouped[categoryId] = [];
      }
      grouped[categoryId].push(item);
    });

    return grouped;
  }, [filteredItems]);

  const scrollToCTA = () => {
    const ctaSection = document.getElementById("membership");
    if (ctaSection) {
      const offset = 80;
      const elementPosition = ctaSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-background to-background-end -z-10" />

      <motion.div className="absolute inset-0 opacity-5 -z-5" style={{ y }}>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(124,179,66,0.3),transparent_50%)]" />
      </motion.div>

      <motion.div
        className="container mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-32 text-center"
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
      >
        <motion.div
          variants={fadeInUp}
          className="flex justify-center mb-10 sm:mb-14 lg:mb-16"
        >
          <Image
            src={logoSrc}
            alt="idworks Hub"
            width={240}
            height={120}
            className="w-auto h-16 sm:h-20 md:h-24 lg:h-32"
            priority
          />
        </motion.div>

        <motion.h1
          variants={fadeInUp}
          className="text-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-6 sm:mb-8 leading-tight px-4"
        >
          A Creative Space for
          <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-primary to-green-600 animate-gradient">
            Design Thinkers
          </span>
        </motion.h1>

        <motion.p
          variants={fadeInUp}
          className="text-text text-base sm:text-lg md:text-xl max-w-2xl mx-auto mb-8 sm:mb-10 lg:mb-12 px-4 leading-relaxed"
        >
          Where innovation meets collaboration. Join Riyadh's premier creative
          hub for designers, entrepreneurs, and visionaries.
        </motion.p>

        <motion.div
          variants={fadeInUp}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8 sm:mb-10"
        >
          <motion.button
            onClick={scrollToCTA}
            className="group relative px-8 py-4 bg-gradient-to-r from-primary to-green-600 text-white font-semibold rounded-full overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span className="relative z-10 flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Become an Idworker
            </span>
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-green-600 to-primary"
              initial={{ x: "-100%" }}
              whileHover={{ x: 0 }}
              transition={{ duration: 0.3 }}
            />
          </motion.button>

          <motion.button
            onClick={() => setMenuOpen(true)}
            className="group relative px-8 py-4 bg-card border-2 border-primary/20 text-heading font-semibold rounded-full hover:bg-primary/5 hover:border-primary transition-all duration-300 shadow-lg"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span className="flex items-center gap-2">
              <Menu className="w-5 h-5" />
              View Menu
            </span>
          </motion.button>

          <motion.button
            onClick={scrollToEvents}
            className="group relative px-8 py-4 bg-card border-2 border-primary/20 text-heading font-semibold rounded-full hover:bg-primary/5 hover:border-primary transition-all duration-300 shadow-lg"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Events
            </span>
          </motion.button>
        </motion.div>

        <motion.div
          variants={fadeInUp}
          className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 rounded-full bg-card/80 backdrop-blur-xl border border-border shadow-lg"
        >
          <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
          <span className="text-heading font-semibold text-sm sm:text-base">
            KAFD, Riyadh
          </span>
        </motion.div>

        <motion.div
          className="absolute top-1/4 left-4 sm:left-10 w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-primary/10 blur-3xl hidden sm:block"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute bottom-1/4 right-4 sm:right-10 w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-primary/10 blur-3xl hidden sm:block"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.5, 0.3, 0.5],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </motion.div>

      <motion.div
        className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2"
        animate={{
          y: [0, 10, 0],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        <div className="w-5 h-8 sm:w-6 sm:h-10 rounded-full border-2 border-primary/30 flex items-start justify-center p-1.5 sm:p-2">
          <motion.div
            className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-primary"
            animate={{
              y: [0, 12, 0],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </div>
      </motion.div>

      {/* MENU DIALOG */}
      <Dialog open={menuOpen} onOpenChange={setMenuOpen}>
        {/* Overlay when cart is open */}
        {cartOpen && (
          <div
            className="fixed inset-0 bg-black/60 z-[10001] backdrop-blur-sm transition-all duration-300"
            onClick={() => setCartOpen(false)}
          />
        )}

        <DialogContent className="w-full h-full sm:w-[95vw] sm:h-[90vh] sm:max-w-[1400px] sm:rounded-3xl p-0 gap-0 overflow-hidden z-[10000] bg-card dark:bg-card flex flex-col">
          <div className="flex-shrink-0 bg-card dark:bg-card border-b border-border p-4 sm:p-6 lg:p-8 shadow-sm">
            <div className="flex items-start justify-between mb-4">
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <DialogTitle className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-primary via-green-600 to-primary bg-clip-text text-transparent mb-2">
                  Our Menu
                </DialogTitle>
                <DialogDescription className="text-text text-xs sm:text-sm lg:text-base">
                  Discover our premium selection of beverages and snacks
                </DialogDescription>
              </motion.div>

              {/* Cart Button */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.2 }}
                className="mt-7"
              >
                <CartButton onOpenChange={setCartOpen} />
              </motion.div>
            </div>

            <motion.div
              className="relative mb-4"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search menu items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 sm:pl-12 pr-10 sm:pr-12 py-3 sm:py-4 rounded-xl sm:rounded-2xl border-2 border-border focus:border-primary focus:outline-none transition-all bg-muted text-heading placeholder:text-muted-foreground text-sm sm:text-base"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-heading transition-colors"
                >
                  <X className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              )}
            </motion.div>

            <motion.div
              className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <button
                onClick={() => setSelectedCategory("All")}
                className={`flex-shrink-0 px-4 sm:px-6 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-semibold whitespace-nowrap transition-all duration-300 ${
                  selectedCategory === "All"
                    ? "bg-gradient-to-r from-primary to-green-600 text-white shadow-lg shadow-primary/30 scale-105"
                    : "bg-card text-muted-foreground hover:bg-muted border-2 border-border"
                }`}
              >
                All Items
              </button>
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex-shrink-0 px-4 sm:px-6 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-semibold whitespace-nowrap transition-all duration-300 ${
                    selectedCategory === category.id
                      ? "bg-gradient-to-r from-primary to-green-600 text-white shadow-lg shadow-primary/30 scale-105"
                      : "bg-card text-muted-foreground hover:bg-muted border-2 border-border"
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </motion.div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 space-y-6 sm:space-y-8 bg-muted/30">
            {menuLoading && (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-10 h-10 text-primary animate-spin" />
              </div>
            )}

            {menuError && (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                  <X className="w-8 h-8 text-red-600" />
                </div>
                <p className="text-red-600 mb-4">{menuError}</p>
                <button
                  onClick={refetchMenu}
                  className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                >
                  Try Again
                </button>
              </div>
            )}

            {!menuLoading && !menuError && filteredItems.length === 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-12 sm:py-20"
              >
                <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                  <Search className="w-8 h-8 sm:w-10 sm:h-10 text-muted-foreground" />
                </div>
                <p className="text-muted-foreground text-base sm:text-lg">
                  No items found
                </p>
              </motion.div>
            )}

            {!menuLoading &&
              !menuError &&
              categories.map((category) => {
                const items = itemsByCategory[category.id] || [];
                if (items.length === 0) return null;

                return (
                  <motion.div
                    key={category.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                  >
                    <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-5">
                      <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-primary">
                        {category.name}
                      </h3>
                      <div className="flex-1 h-px bg-gradient-to-r from-primary/30 to-transparent"></div>
                      <span className="text-xs sm:text-sm text-muted-foreground font-medium">
                        {items.length} items
                      </span>
                    </div>

                    <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                      {items.map((item, idx) => {
                        const itemInCart = getItemCount(item.id);
                        const justAdded = addedItems.has(item.id);
                        const currentQuantity = getQuantity(item.id);

                        return (
                          <motion.div
                            key={item.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.03 }}
                            whileHover={{ y: -5 }}
                            className="group relative p-4 sm:p-5 rounded-xl sm:rounded-2xl bg-card hover:bg-muted border-2 border-border hover:border-primary/50 transition-all duration-300 shadow-sm hover:shadow-lg"
                          >
                            <div className="absolute inset-0 rounded-xl sm:rounded-2xl bg-gradient-to-br from-primary/0 to-primary/3 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                            <div className="relative z-10">
                              {/* Item Info */}
                              <div className="flex justify-between items-start mb-2 gap-2">
                                <h4 className="font-bold text-heading group-hover:text-primary transition-colors text-sm sm:text-base lg:text-lg flex-1">
                                  {item.name}
                                </h4>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <span className="text-primary font-bold text-base sm:text-lg lg:text-xl whitespace-nowrap">
                                    {item.price}
                                  </span>
                                  <span className="text-primary/70 font-semibold text-xs sm:text-sm">
                                    SR
                                  </span>
                                </div>
                              </div>
                              <p className="text-xs sm:text-sm text-muted-foreground text-right font-medium mb-3">
                                {item.name_ar}
                              </p>

                              {/* Quantity Controls */}
                              <div className="flex items-center gap-2 mb-3">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateLocalQuantity(
                                      item.id,
                                      currentQuantity - 1,
                                    );
                                  }}
                                  className="w-8 h-8 rounded-lg bg-muted border border-border hover:border-primary hover:bg-primary/5 flex items-center justify-center text-heading transition-all"
                                >
                                  <Minus className="w-4 h-4" />
                                </button>

                                <input
                                  type="number"
                                  min="1"
                                  max="99"
                                  value={currentQuantity}
                                  onChange={(e) => {
                                    const val = parseInt(e.target.value) || 1;
                                    updateLocalQuantity(item.id, val);
                                  }}
                                  className="flex-1 text-center font-semibold text-heading bg-muted border border-border rounded-lg py-1.5 focus:border-primary focus:outline-none"
                                />

                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateLocalQuantity(
                                      item.id,
                                      currentQuantity + 1,
                                    );
                                  }}
                                  className="w-8 h-8 rounded-lg bg-muted border border-border hover:border-primary hover:bg-primary/5 flex items-center justify-center text-heading transition-all"
                                >
                                  <Plus className="w-4 h-4" />
                                </button>
                              </div>

                              {/* Add to Cart Button */}
                              <motion.button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAddToCart(item);
                                }}
                                className={`w-full py-2 px-3 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 transition-all ${
                                  justAdded
                                    ? "bg-green-500 text-white"
                                    : "bg-primary hover:bg-primary/90 text-white"
                                }`}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                              >
                                {justAdded ? (
                                  <>
                                    <Check className="w-4 h-4" />
                                    <span>Added!</span>
                                  </>
                                ) : (
                                  <>
                                    <ShoppingCart className="w-4 h-4" />
                                    <span>Add to Cart</span>
                                    {itemInCart > 0 && (
                                      <span className="ml-1 px-1.5 py-0.5 bg-white/20 rounded-full text-xs">
                                        {itemInCart}
                                      </span>
                                    )}
                                  </>
                                )}
                              </motion.button>
                            </div>

                            <motion.div
                              className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-primary to-green-600 rounded-b-xl sm:rounded-b-2xl"
                              initial={{ scaleX: 0 }}
                              whileHover={{ scaleX: 1 }}
                              transition={{ duration: 0.3 }}
                            />
                          </motion.div>
                        );
                      })}
                    </div>
                  </motion.div>
                );
              })}
          </div>
        </DialogContent>
      </Dialog>

      {/* EVENTS DIALOG */}
      <Dialog open={eventsOpen} onOpenChange={setEventsOpen}>
        <DialogContent className="w-full h-auto max-h-[90vh] sm:w-[90vw] md:w-[80vw] lg:w-[70vw] max-w-[900px] sm:rounded-3xl z-[10000] overflow-y-auto">
          <div className="flex justify-between items-start mb-4">
            <DialogHeader>
              <DialogTitle className="text-2xl sm:text-3xl font-bold text-center bg-gradient-to-r from-primary to-green-600 bg-clip-text text-transparent mb-2">
                Upcoming Events
              </DialogTitle>
            </DialogHeader>
          </div>

          <motion.div
            className="relative p-6 sm:p-8 lg:p-12 rounded-2xl overflow-hidden"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-primary/10 to-transparent" />
            <motion.div
              className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(124,179,66,0.2),transparent_70%)]"
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.3, 0.6, 0.3],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />

            <div className="relative z-10 text-center">
              <motion.div
                className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-primary/10 mb-4 sm:mb-6"
                animate={{
                  rotate: [0, 360],
                }}
                transition={{
                  duration: 20,
                  repeat: Infinity,
                  ease: "linear",
                }}
              >
                <Calendar className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
              </motion.div>

              <h3 className="text-heading text-xl sm:text-2xl lg:text-3xl font-bold mb-3 sm:mb-4">
                Coming Soon
              </h3>
              <p className="text-text text-sm sm:text-base lg:text-lg max-w-xl mx-auto mb-6 sm:mb-8 leading-relaxed px-4">
                Exciting events, workshops, and exhibitions are on the way. Stay
                tuned for announcements!
              </p>

              <div className="flex justify-center gap-3 sm:gap-4">
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    animate={{
                      y: [0, -10, 0],
                      opacity: [0.5, 1, 0.5],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: i * 0.3,
                    }}
                  >
                    <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
